import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { getTracks, getBrowserData, getMyPlayDates } from '../api';
import usePlayerStore from '../store/playerStore';
import NavControls from '../components/NavControls';
import useSortable from '../hooks/useSortable';
import TrackTable from '../components/TrackTable';

// Weighted shuffle — tracks not heard in a long time (or never) float to the
// front.  Each track gets a random score multiplied by how many days since it
// was last played (never-played tracks are treated as 9999 days old).  Sorting
// descending by that score means old/unheard tracks almost always come first,
// but there's still genuine randomness — a recently-played track can get lucky.
// This works forever, not just until everything has one play.
const MS_PER_DAY = 86_400_000;
const NEVER_PLAYED_DAYS = 9_999;

function weightedShuffle(tracks, playDates = {}) {
  const now = Date.now();
  return tracks
    .map(t => {
      const userLastPlayed = playDates[t.id];
      const daysAgo = userLastPlayed
        ? (now - new Date(userLastPlayed).getTime()) / MS_PER_DAY
        : NEVER_PLAYED_DAYS;
      return { track: t, score: Math.random() * daysAgo };
    })
    .sort((a, b) => b.score - a.score)
    .map(x => x.track);
}

function MultiSelectPanel({ title, items, selected, onSelect }) {
  const allSelected = selected.length === 0;
  const [dragStart, setDragStart] = useState(null);
  const [dragMode, setDragMode] = useState(null); // 'add' or 'remove'

  const handleMouseDown = (item, e) => {
    if (item === '__all__') {
      onSelect([]);
      return;
    }

    // Always additive — toggle the clicked item (works on mobile and desktop)
    const isSelected = selected.includes(item);
    const mode = isSelected ? 'remove' : 'add';
    setDragStart(item);
    setDragMode(mode);

    if (isSelected) {
      onSelect(selected.filter(s => s !== item));
    } else {
      onSelect([...selected, item]);
    }

    e.preventDefault();
  };

  const handleMouseEnter = (item) => {
    if (dragStart === null || item === '__all__') return;

    // Select range from dragStart to current item
    const startIdx = items.indexOf(dragStart);
    const endIdx = items.indexOf(item);
    if (startIdx === -1 || endIdx === -1) return;

    const from = Math.min(startIdx, endIdx);
    const to = Math.max(startIdx, endIdx);
    const range = items.slice(from, to + 1);

    if (dragMode === 'remove') {
      onSelect(selected.filter(s => !range.includes(s)));
    } else {
      // Merge with existing selection
      const merged = [...new Set([...selected, ...range])];
      onSelect(merged);
    }
  };

  const handleMouseUp = () => {
    setDragStart(null);
    setDragMode(null);
  };

  // Attach mouseup globally so drag ends even if mouse leaves panel
  React.useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, []);

  return (
    <div className="browser-panel">
      <div className="browser-panel-header">
        {title}
        {selected.length > 0 && (
          <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--accent)' }}>
            {selected.length} selected
          </span>
        )}
      </div>
      <div className="browser-panel-list" style={{ userSelect: 'none' }}>
        <div
          className={`browser-panel-item ${allSelected ? 'selected' : ''}`}
          onMouseDown={(e) => handleMouseDown('__all__', e)}
        >
          All ({items.length} {title}s)
        </div>
        {items.map(item => (
          <div
            key={item}
            className={`browser-panel-item ${selected.includes(item) ? 'selected' : ''}`}
            onMouseDown={(e) => handleMouseDown(item, e)}
            onMouseEnter={() => handleMouseEnter(item)}
          >
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}

function YearGridPanel({ years, selected, onSelect }) {
  const [dragStart, setDragStart] = useState(null);
  const [dragMode, setDragMode] = useState(null);

  const handleMouseDown = (year, e) => {
    // Always additive — toggle the clicked year (works on mobile and desktop)
    const isSelected = selected.includes(year);
    const mode = isSelected ? 'remove' : 'add';
    setDragStart(year);
    setDragMode(mode);

    if (isSelected) {
      onSelect(selected.filter(s => s !== year));
    } else {
      onSelect([...selected, year]);
    }
    e.preventDefault();
  };

  const handleMouseEnter = (year) => {
    if (dragStart === null) return;
    const startIdx = years.indexOf(dragStart);
    const endIdx = years.indexOf(year);
    if (startIdx === -1 || endIdx === -1) return;
    const from = Math.min(startIdx, endIdx);
    const to = Math.max(startIdx, endIdx);
    const range = years.slice(from, to + 1);
    if (dragMode === 'remove') {
      onSelect(selected.filter(s => !range.includes(s)));
    } else {
      onSelect([...new Set([...selected, ...range])]);
    }
  };

  const handleMouseUp = () => {
    setDragStart(null);
    setDragMode(null);
  };

  React.useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, []);

  const allSelected = selected.length === 0;

  return (
    <div className="browser-panel" style={{ flex: 2 }}>
      <div className="browser-panel-header">
        Year
        {selected.length > 0 && (
          <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--accent)' }}>
            {selected.length} selected
          </span>
        )}
      </div>
      <div style={{ padding: '8px', overflowY: 'auto', flex: 1 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(52px, 1fr))',
            gap: 4,
            userSelect: 'none',
          }}
        >
          <div
            onMouseDown={(e) => { onSelect([]); e.preventDefault(); }}
            style={{
              gridColumn: '1 / -1',
              padding: '4px 8px',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              textAlign: 'center',
              background: allSelected ? 'var(--accent)' : 'var(--bg3)',
              color: allSelected ? '#fff' : 'var(--text-muted)',
              border: '1px solid var(--border)',
            }}
          >
            All Years
          </div>
          {years.map(year => {
            const isSelected = selected.includes(year);
            return (
              <div
                key={year}
                onMouseDown={(e) => handleMouseDown(year, e)}
                onMouseEnter={() => handleMouseEnter(year)}
                style={{
                  padding: '4px 2px',
                  borderRadius: 6,
                  fontSize: 12,
                  cursor: 'pointer',
                  textAlign: 'center',
                  background: isSelected ? 'var(--accent)' : 'var(--bg3)',
                  color: isSelected ? '#fff' : 'var(--text)',
                  border: `1px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`,
                  fontWeight: isSelected ? 600 : 400,
                  transition: 'background 0.1s',
                }}
              >
                {year}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StarRating({ rating }) {
  const stars = Math.round(rating / 20);
  return (
    <div className="stars">
      {[1,2,3,4,5].map(i => (
        <span key={i} className={`star ${i <= stars ? 'filled' : ''}`}>★</span>
      ))}
    </div>
  );
}

export default function BestOf() {
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [selectedYears, setSelectedYears] = useState([]);
  const { playQueue } = usePlayerStore();

  // Fetch all loved tracks
  const { data, isLoading } = useQuery(
    'bestof-tracks',
    () => getTracks({ is_loved: true, page_size: 30000 }).then(r => r.data),
    { staleTime: 5 * 60 * 1000 }
  );

  // Per-user last-played dates: { track_id: iso_string }
  // Falls back to an empty object so the shuffle still works when loading.
  const { data: playDates = {} } = useQuery(
    'my-play-dates',
    () => getMyPlayDates().then(r => r.data),
    { staleTime: 5 * 60 * 1000 }
  );

  const allTracks = data?.results || [];

  // Build genre and year lists from loved tracks only
  const genres = useMemo(() => {
    const set = new Set(allTracks.map(t => t.genre_name).filter(Boolean));
    return [...set].sort();
  }, [allTracks]);

  const years = useMemo(() => {
    const set = new Set(allTracks.map(t => t.year).filter(Boolean));
    return [...set].sort((a, b) => b - a); // newest first
  }, [allTracks]);

  // Filter tracks based on selections
  const filteredTracks = useMemo(() => {
    return allTracks.filter(t => {
      if (selectedGenres.length > 0 && !selectedGenres.includes(t.genre_name)) return false;
      if (selectedYears.length > 0 && !selectedYears.includes(t.year)) return false;
      return true;
    });
  }, [allTracks, selectedGenres, selectedYears]);

  const { sorted, handleSort, SortIcon } = useSortable(filteredTracks, 'artist_name');

  // Display order: tracks this user has never played first, then oldest-heard →
  // newest-heard, using the user's personal play history.
  const sortedOldestFirst = useMemo(() => {
    const now = Date.now();
    return [...sorted].sort((a, b) => {
      const aDays = playDates[a.id] ? (now - new Date(playDates[a.id]).getTime()) / MS_PER_DAY : NEVER_PLAYED_DAYS;
      const bDays = playDates[b.id] ? (now - new Date(playDates[b.id]).getTime()) / MS_PER_DAY : NEVER_PLAYED_DAYS;
      return bDays - aDays; // most-neglected by this user first
    });
  }, [sorted, playDates]);

  const handlePlay = () => playQueue(sortedOldestFirst, 0);

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h1>⭐ Best Of</h1>
          <NavControls />
        </div>
        <p>{isLoading ? 'Loading…' : `${filteredTracks.length} loved tracks`}</p>
      </div>

      <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--text-muted)' }}>
        Tap to select/deselect — selections are additive. Drag to select a range. Tap <strong>All</strong> to clear.
      </div>

      <div className="browser-bar" style={{ height: 200 }}>
        <MultiSelectPanel
            title="Genre"
            items={genres}
        selected={selectedGenres}
        onSelect={setSelectedGenres}
        />
        <YearGridPanel
            years={years}
            selected={selectedYears}
            onSelect={setSelectedYears}
        />
      </div>

      {isLoading ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading your loved tracks…</p>
      ) : (
        <TrackTable
          tracks={sortedOldestFirst}
          queryKey="bestof-tracks"
          onShuffle={() => weightedShuffle(filteredTracks, playDates)}
        />
      )}
    </div>
  );
}